// Откат из истории: кто может, предупреждение о более поздних правках, восстановление с каскадом. Общий для экрана
// и вкладки в карточке; запись — обычный apply_changes, ответ приходит журналом.
import { confirmDialog } from '@core/ui/confirm';
import { useCallback, useMemo, type ReactNode } from 'react';
import { useAuth } from '@core/auth/store';
import { data } from '@core/store';
import { dateTimeLabel } from '@core/dates';
import { canRevert, formatValue, planRevert, planRevertAction, type Action, type HistoryRow, type Resolve } from './planner';
import { laterChanges, liveRow, txRows } from './useHistory';
import { useActor, useResolve } from './resolve';

type Opts = { rows: HistoryRow[]; reload: () => void; fail: (err?: string) => void };

export function useRevert({ rows, reload, fail }: Opts) {
  const profile = useAuth((s) => s.profile);
  const me = useMemo(() => (profile ? { id: profile.id, role: profile.role } : null), [profile]);
  const actorOf = useActor();
  const resolve: Resolve = useResolve();
  // кого откатили: строка журнала → кто и когда её откатил (для бейджа «reverted by …»)
  const revertedBy = useMemo(() => new Map(rows.flatMap((r) => (typeof r.op?.['log_id'] === 'number' ? [[r.op['log_id'] as number, { id: r.id, at: r.at, actor: r.actor }] as const] : []))), [rows]);

  const run = useCallback(async (planned: { changes: Parameters<typeof data.applyChanges>[0]; op: Parameters<typeof data.applyChanges>[1] }) => {
    const r = await data.applyChanges(planned.changes, planned.op);
    if (!r.ok) fail(r.conflict ? 'Someone changed this just now — the history is refreshed, try again.' : r.error);
    reload();
  }, [fail, reload]);

  // «после этого были ещё изменения»: последняя правка по тем же полям — кто, когда, что
  /** Абзац о более поздних правках тех же полей — если они есть; иначе null. */
  const laterNote = useCallback(async (row: HistoryRow): Promise<ReactNode> => {
    if (row.kind !== 'update') return null;
    const later = await laterChanges(row);
    if (later.length === 0) return null;
    const last = later[later.length - 1]!;
    const f = last.changed.find((x) => row.changed.includes(x)) ?? last.changed[0] ?? '';
    const what = last.kind === 'update' ? `${f}: ${formatValue(f, last.old?.[f], resolve)} → ${formatValue(f, last.new?.[f], resolve)}` : last.kind;
    return <p>After this change there {later.length === 1 ? 'was another change' : `were ${later.length} more changes`}; the last one — <b>{actorOf(last.actor).name}</b>, {dateTimeLabel(last.at)}: {what}. Reverting will overwrite it.</p>;
  }, [actorOf, resolve]);
  const whose = (actor: string | null, at: string) => <><b>{actorOf(actor).name}</b>, {dateTimeLabel(at)}</>;

  const revertRow = useCallback(async (row: HistoryRow) => {
    try {
      const note = await laterNote(row);
      // журнал в истории новее стора: дочитываем, иначе только что удалённая строка кажется живой («Already restored»)
      await data.catchUp().catch(() => undefined);
      const same = row.kind === 'delete' ? await txRows(row.tx) : [];
      const p = planRevert(row, liveRow(row.table_name, row.row_id), same);
      if ('error' in p) { fail(p.error); return; }
      const label = row.row_label ?? 'this object';
      const ok = await confirmDialog(row.kind === 'delete'
        ? { title: 'Restore', text: <p>Restore «{label}», deleted by {whose(row.actor, row.at)}?</p>, yes: 'Restore' }
        : row.kind === 'insert'
          ? { title: 'Revert', text: <><p>Delete «{label}» again — it was added by {whose(row.actor, row.at)}? It stays in the history.</p>{note}</>, yes: 'Delete', danger: true }
          : { title: 'Revert', text: <><p>Revert the change by {whose(row.actor, row.at)} on «{label}»?</p>{note}</>, yes: note ? 'Revert anyway' : 'Revert', danger: !!note });
      if (!ok) return;
      await run(p);
    } catch (e) { fail((e as Error).message); }
  }, [laterNote, run, fail, actorOf]);

  const revertAll = useCallback(async (action: Action) => {
    try {
      const notes = (await Promise.all(action.rows.map(laterNote))).filter(Boolean);
      await data.catchUp().catch(() => undefined);
      const p = planRevertAction(action, (r) => liveRow(r.table_name, r.row_id));
      if ('error' in p) { fail(p.error); return; }
      const ok = await confirmDialog({
        title: 'Revert', yes: notes.length ? 'Revert anyway' : 'Revert', danger: notes.length > 0,
        text: <><p>Revert «{action.label}» by {whose(action.actor, action.at)} — {action.rows.length} rows?</p>{notes[0]}</>,
      });
      if (!ok) return;
      await run(p);
    } catch (e) { fail((e as Error).message); }
  }, [laterNote, run, fail, actorOf]);

  const revertOf = useCallback((row: HistoryRow) => {
    const can = canRevert(row, me);
    return { label: row.kind === 'delete' ? 'Restore' : 'Revert', disabled: can.ok ? undefined : can.why, run: () => void revertRow(row) };
  }, [me, revertRow]);
  const revertActionOf = useCallback((action: Action) => {
    const bad = action.rows.map((r) => canRevert(r, me)).find((c) => !c.ok);
    return { disabled: bad && !bad.ok ? bad.why : undefined, run: () => void revertAll(action) };
  }, [me, revertAll]);

  return { revertOf, revertActionOf, revertedBy, actorOf, resolve };
}
